import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Stack, styled, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useResetPasswordMutation } from "Auth/Api/Mutations/useResetPasswordMutation";
import { BlFormInput } from "Forms/Components/BlFormInput";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { Spacing } from "Infrastructure/Themes/Themes";
import {
  getTranslation,
  Resources,
  useResource,
} from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { BlButton } from "Shared/Buttons/BlButton";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";

const ResetPasswordSchema = z.object({
  login: z.email({
    error: () => getTranslation(Resources.Validation.InvalidEmail),
  }),
});

type ResetPasswordFormData = z.infer<typeof ResetPasswordSchema>;

const Form = styled("form")`
  width: 100%;
  max-width: 400px;
`;

export const ResetPasswordForm = (): React.ReactNode => {
  const theme = useTheme();
  const { t } = useResource();

  const { mutate, error } = useResetPasswordMutation();

  const [isSuccess, setIsSuccess] = useState(false);

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: { login: "" },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  useHandleServerError({
    form,
    error,
    formKey: "login",
    resource: Resources.SignIn.Error,
  });

  const submit = (data: ResetPasswordFormData) => {
    mutate({ login: data.login }, { onSuccess: () => setIsSuccess(true) });
  };

  if (isSuccess) {
    return (
      <Box sx={{ width: "100%", maxWidth: "600px" }}>
        <Typography variant="h1" mb={Spacing.M}>
          {t(Resources.ResetPassword.Success.Title)}
        </Typography>
        <Typography
          variant="body1"
          color={theme.palette.text.secondary}
          mb={Spacing.L}
        >
          {t(Resources.ResetPassword.Success.Description)}
        </Typography>
        <UnstyledLink to={AppRouting.SignIn.path()} isUnderlined>
          <Typography variant="body2">
            {t(Resources.ResetPassword.BackToSignIn)}
          </Typography>
        </UnstyledLink>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "400px" }}>
      <Typography variant="h1" mb={Spacing.S}>
        {t(Resources.ResetPassword.Title)}
      </Typography>
      <Typography
        variant="body2"
        color={theme.palette.text.secondary}
        mb={Spacing.L}
      >
        {t(Resources.ResetPassword.Subtitle)}
      </Typography>
      <Form onSubmit={handleSubmit(submit)}>
        <Stack spacing={Spacing.L}>
          <BlFormInput
            control={control}
            name="login"
            errors={errors}
            type="email"
            autoComplete="email"
            label={t(Resources.ResetPassword.Email.Label)}
            placeholder={t(Resources.ResetPassword.Email.Label)}
          />
          <Stack spacing={Spacing.XS}>
            <BlButton type="submit" fullWidth color="primary">
              {t(Resources.ResetPassword.Button)}
            </BlButton>
            <Box display="flex" justifyContent="center" alignItems="center">
              <UnstyledLink to={AppRouting.SignIn.path()} isUnderlined>
                <Typography variant="body2">
                  {t(Resources.ResetPassword.BackToSignIn)}
                </Typography>
              </UnstyledLink>
            </Box>
          </Stack>
        </Stack>
      </Form>
    </Box>
  );
};
